import m from "mithril";
import PaginationBar from "./PaginationBar.js";

/**
 * Shared presentational paginated table with expandable detail rows.
 *
 * This component is purely presentational: it holds NO state and performs NO
 * fetches. All data and interactions are passed in via `state`, which is owned
 * by the parent (matching the BadanDanUnitUsaha multi-tab pattern). This keeps
 * async fetch redraws out of any component that may be swapped in/out when a
 * tab changes, avoiding DOM reconciliation races.
 *
 * Props:
 *   state          { data, loading, expandedRowId, details, loadingDetails,
 *                    page, totalPages, totalRecords, limit } driven by the parent,
 *                    plus callbacks the parent wires onto it: state.toggle(id)
 *                    and state.goToPage(page).
 *   columns        [{ label, align?, cellClass?, cell(item) }] (action column added automatically)
 *   renderDetail   (rows) => vnode                    (content of the expanded row)
 *   rowKey         (item) => id                       (default: item.customer_id)
 *   itemLabel      string                             (PaginationBar text)
 *   emptyText      string                             (empty list message)
 *   filterBar      (state) => vnode | null            (rendered above the table)
 *   totalsBar      (state) => vnode | null            (rendered below the table)
 */
const PaginatedExpandableTable = {
    view: function (vnode) {
        const { state, columns, renderDetail } = vnode.attrs;
        const rowKey = vnode.attrs.rowKey || ((item) => item.customer_id);
        const itemLabel = vnode.attrs.itemLabel || "data";
        const emptyText = vnode.attrs.emptyText || "Tidak ada data";
        const filterBar = vnode.attrs.filterBar;
        const totalsBar = vnode.attrs.totalsBar;
        const colSpan = columns.length + 2; // +1 "No" column, +1 action column

        const thClass = "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider";
        const alignClass = (col) => col.align === "right" ? " text-right" : col.align === "center" ? " text-center" : "";
        const tdClass = (col) => {
            const align = col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "";
            return `px-6 py-4 whitespace-nowrap text-sm ${col.cellClass || "text-gray-700 dark:text-gray-500"} ${align}`;
        };

        return m("div", [
            filterBar ? filterBar(state) : null,

            state.loading ? m("div", { class: "text-center p-4 text-gray-500" }, "Memuat data...") :
                m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden" }, [
                    m("div", { class: "overflow-x-auto" }, [
                        m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                            m("thead", { class: "bg-gray-50 dark:bg-gray-700" }, [
                                m("tr", [
                                    m("th", { class: thClass }, "No"),
                                    ...columns.map(col =>
                                        m("th", { class: `${thClass}${alignClass(col)}` }, col.label)
                                    ),
                                    m("th", { class: `${thClass} text-center` }, "Aksi")
                                ])
                            ]),
                            m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" },
                                state.data.length === 0 ?
                                    m("tr", [
                                        m("td", { colspan: colSpan, class: "px-6 py-4 text-center text-gray-500 dark:text-gray-400" }, emptyText)
                                    ]) :
                                    state.data.map((item, index) => {
                                        const id = rowKey(item);
                                        return [
                                            m("tr", {
                                                class: `hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${state.expandedRowId === id ? "bg-orange-50 dark:bg-gray-700" : ""}`,
                                                onclick: () => state.toggle(id)
                                            }, [
                                                m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" }, index + 1),
                                                ...columns.map(col =>
                                                    m("td", { class: tdClass(col) }, col.cell(item))
                                                ),
                                                m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-center" },
                                                    m("button", {
                                                        class: "text-orange-500 hover:text-orange-700 focus:outline-none",
                                                        title: "Lihat Rincian"
                                                    }, m("i", { class: `ri-arrow-${state.expandedRowId === id ? 'up' : 'down'}-s-line text-xl` }))
                                                )
                                            ]),
                                            state.expandedRowId === id ? m("tr", [
                                                m("td", { colspan: colSpan, class: "px-0 py-0 bg-gray-50 dark:bg-gray-900/50" }, [
                                                    m("div", { class: "p-4 pl-12" }, [
                                                        state.loadingDetails[id] ?
                                                            m("div", { class: "text-center py-2 text-gray-500" }, "Memuat rincian...") :
                                                            renderDetail(state.details[id] || [])
                                                    ])
                                                ])
                                            ]) : null
                                        ];
                                    })
                            )
                        ])
                    ]),
                    totalsBar ? totalsBar(state) : null,
                    m(PaginationBar, {
                        page: state.page,
                        totalPages: state.totalPages,
                        totalRecords: state.totalRecords,
                        limit: state.limit,
                        itemLabel,
                        onPageChange: (newPage) => state.goToPage(newPage)
                    })
                ])
        ]);
    }
};

export default PaginatedExpandableTable;
